import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EscortProfile, UserRole, SubscriptionTier } from '@/types';
import EscortProfiles from './EscortProfiles';
import UserManagement from './UserManagement';
import ReportedContent, { Report } from './ReportedContent';
import VerificationRequests, { VerificationRequest } from './VerificationRequests';
import AdminTools from './AdminTools';

// Define the AdminUser type
export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  subscriptionTier: SubscriptionTier;
  isVerified: boolean;
  createdAt: Date;
  lastLoginAt?: Date;
  status: 'active' | 'suspended' | 'pending';
}

interface TabConfig {
  value: string;
  label: string;
  content: React.ReactElement;
}

interface AdminTabsProps {
  escorts?: EscortProfile[];
  users?: AdminUser[];
  reportedContent?: Report[];
  verificationRequests?: VerificationRequest[];
  onImageUpload?: (profileId: string, imageFile: File) => Promise<string | void | boolean>;
  onUpdateUserStatus?: (userId: string, status: 'active' | 'suspended' | 'pending') => void;
  onUpdateReportStatus?: (reportId: string, status: string) => void;
  onVerify?: (requestId: string, status: 'approved' | 'rejected') => void;
  // New props for the consolidated admin tools
  isImporting?: boolean;
  isSynchronizing?: boolean;
  onImportData?: () => Promise<void>;
  onSyncAccounts?: () => Promise<void>;
  tabsConfig: TabConfig[];
}

const AdminTabs: React.FC<AdminTabsProps> = ({
  escorts = [],
  users = [],
  reportedContent = [],
  verificationRequests = [],
  onImageUpload,
  onUpdateUserStatus,
  onUpdateReportStatus,
  onVerify,
  isImporting = false,
  isSynchronizing = false,
  onImportData,
  onSyncAccounts,
  tabsConfig
}) => {
  return (
    <Tabs defaultValue="tools" className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="tools">Administration</TabsTrigger>
        <TabsTrigger value="users">Utilisateurs</TabsTrigger>
        <TabsTrigger value="escorts">Escorts</TabsTrigger>
        <TabsTrigger value="reports">Signalements</TabsTrigger>
        <TabsTrigger value="verification">Vérification</TabsTrigger>
      </TabsList>
      
      <TabsContent value="tools">
        <AdminTools 
          isImporting={isImporting}
          isSynchronizing={isSynchronizing}
          onImportData={onImportData || (async () => {})}
          onSyncAccounts={onSyncAccounts || (async () => {})}
          escorts={escorts}
          onImageUpload={onImageUpload}
          users={users}
          onUpdateUserStatus={onUpdateUserStatus}
          reportedContent={reportedContent}
          onUpdateReportStatus={onUpdateReportStatus}
          verificationRequests={verificationRequests}
          onVerify={onVerify}
        />
      </TabsContent>

      <TabsContent value="users">
        {users && users.length > 0 && onUpdateUserStatus && (
          <UserManagement 
            users={users} 
            onUpdateUserStatus={onUpdateUserStatus} 
          />
        )}
      </TabsContent>

      <TabsContent value="escorts">
        {escorts && escorts.length > 0 && (
          <EscortProfiles escorts={escorts} />
        )}
      </TabsContent>

      <TabsContent value="reports">
        {reportedContent && reportedContent.length > 0 && onUpdateReportStatus && (
          <ReportedContent 
            reports={reportedContent} 
            onUpdateReportStatus={onUpdateReportStatus} 
          />
        )}
      </TabsContent>

      <TabsContent value="verification">
        {verificationRequests && verificationRequests.length > 0 && onVerify && (
          <VerificationRequests 
            requests={verificationRequests} 
            onVerify={onVerify} 
          />
        )}
      </TabsContent>
    </Tabs>
  );
};

export default AdminTabs;
